import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";

import { SmartlistType } from "../../../api/MediaAPI.types";
import SmartlistAPI from "../../../api/SmartlistAPI";
import { alphaSortBy } from "../../../utils/sort";
import Button from "../../button/Button";
import { CONTENT_TYPE } from "../../content/Type";
import RelatedList from "../../utils/RelatedList";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  MediaChildren,
  MediaSmartlist,
  MediaSmartlistAdded,
  MediaSmartlistSeason,
  MediaUpdated,
} from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";

const { MASTER } = CONTENT_TYPE;

interface ProjectsByChildContent {
  uuid: string;
  title: string;
  items: Record<string, SmartlistsByProject>;
}

interface SmartlistsByProject {
  items: FormatedSmartlist[];
  title: string;
  uuid: string;
}

interface FormatedSmartlist {
  label: string;
  uuid: string;
  url: string;
  type: string;
}

interface SmartlistListProps {
  smartlistsByProject: Record<string, SmartlistsByProject>;
  deleteSmartlistOnMedia?: (uuid: string) => void;
  emptyText: string;
}

function SmartlistList({
  smartlistsByProject,
  deleteSmartlistOnMedia,
  emptyText,
}: SmartlistListProps): JSX.Element {
  const smartlistsArray = alphaSortBy(
    Object.values(smartlistsByProject),
    "title",
  );

  if (smartlistsArray?.length === 0) {
    return <span>{emptyText}</span>;
  }

  return (
    <div className="flex flex-col gap-[0.9375rem]">
      {smartlistsArray.map((smartlistByProject) => (
        <RelatedList
          key={smartlistByProject.uuid}
          items={smartlistByProject.items}
          onDelete={deleteSmartlistOnMedia}
          title={smartlistByProject.title}
        />
      ))}
    </div>
  );
}

interface Option {
  label: string;
  value: string;
  isDisabled?: boolean;
}

type SmartlistOption = Option & {
  type: SmartlistType;
};

interface AddSmartlistProps {
  addSmartlistOnMedia: (smartlist: MediaSmartlistAdded) => void;
  smartlistsByProject: Record<string, SmartlistsByProject>;
  smartlistTypes: string[];
}

function AddSmartlist({
  addSmartlistOnMedia,
  smartlistsByProject,
  smartlistTypes,
}: AddSmartlistProps): JSX.Element {
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const isSingleProject = projects?.data?.length === 1;

  const [selectedProject, setSelectedProject] = React.useState<Option | null>(
    isSingleProject && projects?.data[0]
      ? {
          label: projects?.data[0].name,
          value: projects?.data[0].uuid,
        }
      : null,
  );

  const [selectedSmartlist, setSelectedSmartlist] =
    React.useState<SmartlistOption | null>(null);

  const getSmartlistListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSmartlistList,
    params: [
      {
        projectSlug: selectedProject?.value ?? "",
      },
    ],
  });

  const { data: smartlists, isLoading } = useQuery({
    ...getSmartlistListQuery,
    enabled: !!selectedProject,
  });

  const { t } = useTranslation("editor-content");

  const isCollection = smartlistTypes.includes("collection");

  const handleChangeProject = (option: SingleValue<Option>): void => {
    if (option) {
      setSelectedProject(option);
    } else {
      setSelectedProject(null);
      setSelectedSmartlist(null);
    }
  };

  const handleChangeSmartlist = (
    option: SingleValue<SmartlistOption>,
  ): void => {
    setSelectedSmartlist(option);
  };

  const addSmartlist = (): void => {
    if (selectedProject && selectedSmartlist) {
      addSmartlistOnMedia({
        uuid: selectedSmartlist.value,
        label: selectedSmartlist.label,
        type: selectedSmartlist.type,
        storefront_project_name: selectedProject.label,
        storefront_project_uuid: selectedProject.value,
      });
      setSelectedProject(null);
      setSelectedSmartlist(null);
    }
  };

  const projectOptions = projects?.data.map((project) => ({
    label: project.name,
    value: project.uuid,
  }));

  const smartlistOptions = smartlists?.data.reduce(
    (
      acc: {
        label: string;
        value: string;
        type: SmartlistType;
        isDisabled: boolean;
      }[],
      smartlist,
    ) => {
      const isSameType = smartlistTypes.includes(smartlist.type);

      if (isSameType) {
        const isDisabled = Boolean(
          selectedProject &&
          smartlistsByProject[selectedProject?.label]?.items?.some(
            (smartlistExist) => smartlistExist.uuid === smartlist.uuid,
          ),
        );

        acc.push({
          label: smartlist.label,
          value: smartlist.uuid,
          type: smartlist.type,
          isDisabled,
        });
      }

      return acc;
    },
    [],
  );

  const selectPlaceholder = isCollection
    ? t("selectCollection")
    : t("selectSmartlist");

  const noOptionsMessage = (): string => {
    if (isCollection) return t("noCollection");
    return t("noSmartlist");
  };

  return (
    <div className="flex gap-[0.46875rem]">
      <Select
        isClearable
        className="editor-select basis-1/3"
        classNamePrefix="slct"
        onChange={handleChangeProject}
        options={projectOptions}
        placeholder={t("selectProject")}
        value={selectedProject}
      />
      <Select
        isClearable
        className="editor-select basis-1/3"
        classNamePrefix="slct"
        isDisabled={!selectedProject}
        isLoading={Boolean(isLoading && selectedProject)}
        noOptionsMessage={noOptionsMessage}
        onChange={handleChangeSmartlist}
        options={smartlistOptions}
        placeholder={selectPlaceholder}
        value={selectedSmartlist}
      />
      <Button
        className="bg-muted h-[36px] px-[1.25rem] py-[0.15625rem] text-[0.703125rem]"
        isDisabled={!selectedProject || !selectedSmartlist}
        onClick={addSmartlist}
        type="button"
      >
        {t("add")}
      </Button>
    </div>
  );
}

interface IMediaMasterSmartlistList {
  mediaChildren?: MediaChildren[];
  emptyText: string;
  smartlistTypes: string[];
  path: string;
}

function MediaMasterSmartlistList({
  mediaChildren,
  emptyText,
  smartlistTypes,
  path,
}: IMediaMasterSmartlistList): JSX.Element {
  const childrenWithSmartlist = mediaChildren?.reduce(
    (acc: ProjectsByChildContent[], currentChild) => {
      const childSmartlist = currentChild.smartlists?.reduce(
        (
          formatedChildSmartlist: Record<string, SmartlistsByProject>,
          smartlist,
        ) => {
          const isSameType = smartlistTypes.includes(smartlist.type);

          if (isSameType) {
            const formatedSmartlist: FormatedSmartlist = {
              label: smartlist.label,
              uuid: smartlist.uuid,
              url: `/projects/${smartlist.storefront_project_name}/${path}/edit/${smartlist.uuid}`,
              type: smartlist.type,
            };

            const currentChildSmartlist =
              formatedChildSmartlist[smartlist.storefront_project_name];

            if (!currentChildSmartlist?.items) {
              // eslint-disable-next-line no-param-reassign
              formatedChildSmartlist[smartlist.storefront_project_name] = {
                title: smartlist.storefront_project_name,
                items: [formatedSmartlist],
                uuid: smartlist.storefront_project_uuid,
              };
            } else {
              // eslint-disable-next-line no-param-reassign
              formatedChildSmartlist[smartlist.storefront_project_name] = {
                ...currentChildSmartlist,
                items: [...currentChildSmartlist.items, formatedSmartlist],
              };
            }
          }

          return formatedChildSmartlist;
        },
        {},
      );

      if (Object.values(childSmartlist)?.length) {
        acc.push({
          uuid: currentChild.uuid,
          title: currentChild.label,
          items: childSmartlist,
        });
      }

      return acc;
    },
    [],
  );

  if (childrenWithSmartlist?.length === 0) {
    return <span>{emptyText}</span>;
  }

  return (
    <div className="flex flex-col gap-[1.5625rem] pt-[0.78125rem] pl-[0.3125rem]">
      {childrenWithSmartlist?.map((smartlistsByChild) => {
        const smartlistsByChildArray = alphaSortBy(
          Object.values(smartlistsByChild.items),
          "title",
        );
        return (
          <div
            key={smartlistsByChild.uuid}
            className="flex flex-col gap-[0.625rem]"
          >
            <span className="font-medium">{smartlistsByChild.title}</span>
            <div className="flex flex-col gap-[0.9375rem]">
              {smartlistsByChildArray?.map((smartlist) => (
                <RelatedList
                  key={smartlist.uuid}
                  items={smartlist.items}
                  title={smartlist.title}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface RelatedSmartlistCommonProps {
  smartlistTypes: SmartlistType[];
  path: string;
  smartlists: (MediaSmartlist | MediaSmartlistSeason)[];
  mediaType: string;
  mediaChildren?: MediaChildren[];
}

interface RelatedSmartlistsProps extends RelatedSmartlistCommonProps {
  onUpdateSmartlistsMedia: (smartlists: MediaUpdated["smartlists"]) => void;
  isReadOnly?: undefined;
}

interface RelatedSmartlistsReadOnlyProps extends RelatedSmartlistCommonProps {
  onUpdateSmartlistsMedia?: undefined;
  isReadOnly: true;
}

function RelatedSmartlists({
  smartlistTypes,
  path,
  smartlists,
  mediaType,
  mediaChildren,
  onUpdateSmartlistsMedia,
  isReadOnly,
}: RelatedSmartlistsProps | RelatedSmartlistsReadOnlyProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-content");
  const isMaster = mediaType === MASTER;
  const isCollection = smartlistTypes.includes(SmartlistType.COLLECTION);
  const isSeason = smartlistTypes.includes(SmartlistType.SEASON);

  const emptyText = useMemo(() => {
    if (isCollection) {
      return t("collectionEmpty");
    }

    if (isSeason) {
      return t("serieEmpty");
    }

    return t("smartlistEmpty");
  }, [isCollection, isSeason, t]);

  const filteredSmartlists = smartlists?.filter((smartlist) => {
    if (smartlist.type === SmartlistType.SEASON) {
      return (
        smartlistTypes.includes(smartlist.type) &&
        Boolean(smartlist.serie_translations)
      );
    }
    return smartlistTypes.includes(smartlist.type);
  });

  const getFormatedSmartlist = (
    smartlist: MediaSmartlist | MediaSmartlistSeason,
  ): FormatedSmartlist => {
    if (smartlist.type === SmartlistType.SEASON) {
      const seasonLabelDefault = smartlist.translations.find(
        (translation) => translation.default,
      )?.name;

      const seasonLabel = smartlist.translations.find(
        (translation) => translation.language === i18n.language,
      )?.name;

      const serieDefault = smartlist.serie_translations?.find(
        (translation) => translation.default,
      );

      const serieLabel = smartlist.serie_translations?.find(
        (translation) => translation.language === i18n.language,
      )?.smartlist_label;

      return {
        label: `${serieLabel ?? serieDefault?.smartlist_label} - ${seasonLabel ?? seasonLabelDefault}`,
        uuid: smartlist.uuid,
        url: `/projects/${smartlist.storefront_project_name}/${path}/edit/${serieDefault?.smartlist_uuid}`,
        type: smartlist.type,
      };
    }

    return {
      label: smartlist.label,
      uuid: smartlist.uuid,
      url: `/projects/${smartlist.storefront_project_name}/${path}/edit/${smartlist.uuid}`,
      type: smartlist.type,
    };
  };

  const smartlistsByProject = filteredSmartlists?.reduce(
    (acc: Record<string, SmartlistsByProject>, value) => {
      const smartlist = getFormatedSmartlist(value);

      const currentSmartlistsByProject = acc[value.storefront_project_name];

      if (!currentSmartlistsByProject?.items) {
        acc[value.storefront_project_name] = {
          title: value.storefront_project_name,
          items: [smartlist],
          uuid: value.storefront_project_uuid,
        };
      } else {
        acc[value.storefront_project_name] = {
          ...currentSmartlistsByProject,
          items: [...currentSmartlistsByProject.items, smartlist],
        };
      }
      return acc;
    },
    {},
  );

  if (isMaster) {
    return (
      <MediaMasterSmartlistList
        emptyText={emptyText}
        mediaChildren={mediaChildren}
        path={path}
        smartlistTypes={smartlistTypes}
      />
    );
  }

  const addSmartlistOnMedia = (smartlist: MediaSmartlistAdded): void => {
    onUpdateSmartlistsMedia?.([...smartlists, smartlist]);
  };

  const deleteSmartlistOnMedia = (uuid: string): void => {
    const updatedSmartlists = smartlists.filter(
      (smartlistExist) => smartlistExist.uuid !== uuid,
    );
    onUpdateSmartlistsMedia?.(updatedSmartlists);
  };

  return (
    <div className="flex flex-col gap-[1.5625rem]">
      <SmartlistList
        emptyText={emptyText}
        smartlistsByProject={smartlistsByProject}
        deleteSmartlistOnMedia={
          onUpdateSmartlistsMedia ? deleteSmartlistOnMedia : undefined
        }
      />
      {!isReadOnly && (
        <AddSmartlist
          addSmartlistOnMedia={addSmartlistOnMedia}
          smartlistsByProject={smartlistsByProject}
          smartlistTypes={smartlistTypes}
        />
      )}
    </div>
  );
}

export default RelatedSmartlists;
